import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { FeatureFlags } from '@assay/shared/featureFlags';

import { api } from './client';
import { queryKeys } from './queryKeys';

export interface UserSettings {
  tutorialCompleted: boolean;
  icp: string | null;
  offering: string | null;
  tone: string | null;
  additionalInstructions: string | null;
  samplesDismissedAt: string | null;
  salesFeaturesEnabled: boolean;
  /**
   * Runtime feature flags already resolved for this user (user > plan > global).
   * Only keys with a rule behind them appear; read them with `useFeatureFlag`
   * rather than indexing this directly.
   */
  featureFlags: FeatureFlags;
}

export function useUserSettings() {
  return useQuery({
    queryKey: queryKeys.userSettings.current(),
    queryFn: () => api.request<UserSettings>('/settings'),
  });
}

export function useSkipTutorial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.request<{ ok: boolean }>('/settings/tutorial/skip', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userSettings.current() });
    },
  });
}

export function useUpdateVoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      icp: string;
      offering: string;
      tone: string;
      additionalInstructions: string;
    }) =>
      api.request<{ ok: boolean }>('/settings/voice', {
        method: 'PUT',
        body: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userSettings.current() });
    },
  });
}

export function useSetSalesFeatures() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.request<{ ok: boolean; salesFeaturesEnabled: boolean }>('/settings/sales-features', {
        method: 'PUT',
        body: { enabled },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userSettings.current() });
    },
  });
}

export function useTutorialComplete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.request<{ ok: boolean }>('/settings/tutorial/complete', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userSettings.current() });
    },
  });
}

export function useDismissSamples() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.request<{ ok: boolean }>('/settings/samples/dismiss', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userSettings.current() });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['outreach'] });
    },
  });
}

export function useResetTutorial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.request<{ ok: boolean }>('/settings/tutorial/reset', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userSettings.current() });
    },
  });
}
